import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { curatedModels } from '../../data/models';
import { builders } from '../../data/builders';
import { cca2Of, countryNames, labCountry, labPrefix, openRouterPrefixCountry } from '../../data/geography';
import { OpenRouterResult } from '../../utils/api';
import { formatReleased } from '../../utils/format';
import { orPrefix } from '../../utils/modelFilters';
import { SourceRef } from '../SourceRef';
interface Maker {
  key: string;
  name: string;
  country: string | null;
  curated: number;
  open: number;
  live: number;
  latest: string | null;
  url: string | null;
}
function buildMakers(or: OpenRouterResult | null): Maker[] {
  const byPrefix = new Map<string, number>();
  (or?.models ?? []).forEach(m => byPrefix.set(orPrefix(m), (byPrefix.get(orPrefix(m)) ?? 0) + 1));
  const makers = new Map<string, Maker>();
  curatedModels.forEach(m => {
    const cur = makers.get(m.lab) ?? {
      key: m.lab,
      name: m.lab,
      country: labCountry[m.lab] ?? null,
      curated: 0,
      open: 0,
      live: 0,
      latest: null,
      url: null
    } as Maker;
    cur.curated += 1;
    if (m.license !== 'proprietary') cur.open += 1;
    if (m.released && (!cur.latest || m.released > cur.latest)) cur.latest = m.released;
    makers.set(m.lab, cur);
  });
  const claimed = new Set<string>();
  makers.forEach(mk => {
    const p = labPrefix[mk.name];
    if (p) {
      mk.live = byPrefix.get(p) ?? 0;
      claimed.add(p);
    }
    const b = builders.find(x => x.org === mk.name || mk.name.startsWith(x.org) || x.org.startsWith(mk.name));
    mk.url = b?.sourceUrl ?? curatedModels.find(m => m.lab === mk.name)?.sourceUrl ?? null;
  });
  byPrefix.forEach((n, p) => {
    if (claimed.has(p)) return;
    makers.set(`or:${p}`, {
      key: `or:${p}`,
      name: p,
      country: openRouterPrefixCountry[p] ?? null,
      curated: 0,
      open: 0,
      live: n,
      latest: null,
      url: `https://openrouter.ai/${p}`
    });
  });
  return Array.from(makers.values()).sort((a, b) => b.curated + b.live - (a.curated + a.live));
}
export function ModelMakers({
  openrouter,
  country
}: {
  openrouter: OpenRouterResult | null;
  country: string;
}) {
  const [showAll, setShowAll] = useState(false);
  const all = useMemo(() => buildMakers(openrouter), [openrouter]);
  const rows = all.filter(m => country === 'all' || m.country === country);
  const visible = showAll ? rows : rows.slice(0, 24);
  return <section aria-labelledby="makers-title" data-id="element-576" data-mp="3momrsha2eskz">
      <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-577" data-mp="3roj0uuxmda7e">
        <div data-id="element-578" data-mp="3rptzyhg4dts6">
          <h2 id="makers-title" className="font-serif text-2xl text-ink md:text-3xl" data-id="element-579" data-mp="3rm69jr5z1nho">
            Model makers
          </h2>
          <p className="mt-1 max-w-2xl text-[13.5px] text-muted" data-id="element-580" data-mp="3hptfgs6il7me">
            {rows.length} labs and publishers{country !== 'all' ? ` in ${countryNames[country] ?? country}` : ' worldwide'}, with how many of their models are listed here and served live.
          </p>
        </div>
      </div>
      <div className="mt-5 overflow-x-auto rounded-lg border border-line" data-id="element-581" data-mp="30gr5su3bgg8e">
        <table className="w-full min-w-[820px] border-collapse text-[13.5px]" data-id="element-582" data-mp="30eo7lms0fgsl">
          <thead className="bg-surface text-left text-[12px] text-muted" data-id="element-583" data-mp="2vjr1ooq11njx">
            <tr data-id="element-584" data-mp="3a7vito9flr0l">
              <th scope="col" className="border-b border-line px-3 py-2.5 font-normal" data-id="element-585" data-mp="3a8ereed6z31y">Maker</th>
              <th scope="col" className="border-b border-line px-3 py-2.5 font-normal" data-id="element-586" data-mp="35ej01urfoefr">Country</th>
              <th scope="col" className="border-b border-line px-3 py-2.5 text-right font-normal" data-id="element-587" data-mp="3j5xio0hfidma">Curated models</th>
              <th scope="col" className="border-b border-line px-3 py-2.5 text-right font-normal" data-id="element-588" data-mp="3f7zt2wjqxpid">Open-weight</th>
              <th scope="col" className="border-b border-line px-3 py-2.5 text-right font-normal" data-id="element-589" data-mp="3t3butnwbkg8s">Served live</th>
              <th scope="col" className="border-b border-line px-3 py-2.5 font-normal" data-id="element-590" data-mp="1yrf1fpebh0b3">Latest release</th>
              <th scope="col" className="border-b border-line px-3 py-2.5 font-normal" data-id="element-591" data-mp="1tt3rzfrxorte">
                <span className="sr-only" data-id="element-592" data-mp="1v9zqdxsj9tj1">Link</span>
              </th>
            </tr>
          </thead>
          <tbody data-id="element-593" data-mp="208kh96lwb0ut">
            {visible.map(m => <tr key={m.key} className="border-b border-line/60 last:border-b-0 hover:bg-surface" data-id="element-594" data-mp="206zkrbg0t7ub">
                <th scope="row" className="whitespace-nowrap px-3 py-2 text-left font-normal text-ink" data-id="element-595" data-mp="258owktb2hpjg">
                  {m.name}
                  {m.url && <SourceRef href={m.url} label={`${m.name} — official page`} grade="A" data-id="element-596" data-mp="1cji1636wtg2f" />}
                </th>
                <td className="whitespace-nowrap px-3 py-2" data-id="element-597" data-mp="1hht742u336fd">
                  {m.country ? <Link to={`/countries/${m.country}`} className="inline-flex items-center gap-2 text-muted hover:text-machine" data-id="element-598" data-mp="1mgw3kyw004bq">
                      {cca2Of[m.country] && <img src={`https://flagcdn.com/${cca2Of[m.country]}.svg`} alt="" loading="lazy" className="h-3 w-4 rounded-[2px] object-cover ring-1 ring-line" data-id="element-599" data-mp="1ra8rt7wrm93y" />}
                      {countryNames[m.country] ?? m.country}
                    </Link> : <span className="text-faint" data-id="element-600" data-mp="3c1l3jn7b1o2h">—</span>}
                </td>
                <td className={`px-3 py-2 text-right font-mono tabular ${m.curated ? 'text-ink' : 'text-faint'}`} data-id="element-601" data-mp="381udjlplrbz0">{m.curated}</td>
                <td className={`px-3 py-2 text-right font-mono tabular ${m.open ? 'text-live' : 'text-faint'}`} data-id="element-602" data-mp="3339fnsxtp4dq">{m.open}</td>
                <td className={`px-3 py-2 text-right font-mono tabular ${m.live ? 'text-ink' : 'text-faint'}`} data-id="element-603" data-mp="2t30h48gcz6v6">{openrouter ? m.live : '…'}</td>
                <td className="whitespace-nowrap px-3 py-2 font-mono text-[12px] text-muted" data-id="element-604" data-mp="2oaz6ss2r3q7s">{m.latest ? formatReleased(m.latest) : '—'}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right" data-id="element-605" data-mp="2fbu9tq6u84xq">
                  {m.url && <a href={m.url} target="_blank" rel="noopener noreferrer" className="text-[12.5px] text-muted hover:text-machine" data-id="element-606" data-mp="2acrbspp9yyz9">
                      Read more →
                    </a>}
                </td>
              </tr>)}
            {rows.length === 0 && <tr data-id="element-607" data-mp="20ovkc0rjjkln">
                <td colSpan={7} className="px-3 py-10 text-center text-muted" data-id="element-608" data-mp="20pf29hh6y8zo">
                  No model makers listed for this country yet.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
      {rows.length > 24 && <button type="button" onClick={() => setShowAll(s => !s)} className="mt-3 rounded-md border border-line px-3 py-1.5 text-sm text-ink hover:border-machine" data-id="element-609" data-mp="2egjva0q20311">
          {showAll ? 'Show top 24' : `Show all ${rows.length}`}
        </button>}
    </section>;
}